import { EventId, MFBEvent } from 'types/models/event';
import IPCamera from 'types/models/ip-camera';
import SamplePoint from 'types/models/samplePoint';
import Trigger from 'types/models/trigger';
import { UserId } from 'types/models/user';

export type NotificationId = number;

export enum NotificationStatus {
  ATTEMPTED = 0, // This should not be used for UI type notifications. They are created with SENT status.
  SENT = 1,
  ACKNOWLEDGED = 2,
  DELIVERY_FAILED = 3
}

export enum NotificationType {
  UI = 1,
  EMAIL = 2,
  SMS = 3
}

export enum NotificationEventType {
  IP_CAMERA_EVENT = 'ip-camera-event',
  FRED_EVENT = 'fred-event',
  SAMPLE_POINT_EVENT = 'sample-point-event'
}

export default interface Notification<E extends SamplePointEvent | IPCameraEvent = SamplePointEvent | IPCameraEvent> {
  id: NotificationId;
  event: E;
  eventId: EventId;
  eventType: NotificationEventType;
  /** uiText for NotificationListItem */
  message: string;
  status: NotificationStatus;
  type: NotificationType;

  /** This field is populated by FE. */
  acknowledgement: {
    user: {
      id: UserId,
      name: string
    },
    at: number;
  } | null
}

// ==============================
// Event-related types that are fitted to Notification's display needs
// ==============================
export type EventSamplePoint = Pick<SamplePoint,
  | 'id'
  | 'sid'
  | 'name'
  | 'siteId'
  | 'deviceId'
>;

export type EventIPCamera = Pick<IPCamera,
  | 'id'
  | 'name'
  | 'siteId'
  | 'landwatchIntegrationId'
  | 'name'
  | 'description'
>;

/**
 * sample-point-event (from NotificationEventType)
 * - will always have an Event
 * - will always have a Sample Point
 */
export interface SamplePointEvent extends Pick<MFBEvent,
  | 'id'
  | 'level'
  | 'message'
  | 'date'
  | 'triggerId'
  | 'triggerValue'
> {
  samplePoint: EventSamplePoint;

  // This JSDoc comment below was from the commit https://bitbucket.org/farmbotteam/farmbot-web-app/commits/9e2f77b85bae8d7fbd5f52a89a283caf6b91773e#Lsrc/models/event.tsT26
  // (PR and Slack link: https://bitbucket.org/farmbotteam/farmbot-web-app/pull-requests/1178)
  // and then got deleted in commit https://bitbucket.org/farmbotteam/farmbot-web-app/commits/0956f4e3b2c4ae9c533a65871fb1475c9f074fdd#Lsrc/models/event.tsF27
  // The gist of it is: via the Alert Triggers drawer tab, the user can clear the input and save, which then deletes
  // the trigger record from our database. Meanwhile, all the previously generated events and notifications associated
  // with this trigger are still there and have the trigger_id. When BE API returns the response, it cannot find a
  // matching trigger record, so the `triggerId` in the response is not null, but the `trigger` is null.

  /** By the time the API response returns the event, the trigger might have been deleted. */
  trigger: Trigger | null;
}

/**
 * ip-camera-event (from NotificationEventType)
 * - will always have an Event
 * - will always have an IP Camera
 */
export interface IPCameraEvent extends Pick<MFBEvent,
  | 'id'
  | 'level'
  | 'date'
> {
  ipCamera: EventIPCamera;
  message: 'SmartHumanMotion' | 'SmartVehicleMotion' | 'PlateNumberRecognition';
}